import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { UploadSimple, DownloadSimple, Spinner, CheckCircle, WarningCircle, FileCsv, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const NONE = "__none__";

export default function ImportAssetsDialog({ open, onOpenChange, onImported }) {
  const [types, setTypes] = useState({});
  const [assetType, setAssetType] = useState("vehicle");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const reqIdRef = useRef(0);

  useEffect(() => { if (open) api.get("/asset-types").then(({ data }) => setTypes(data)).catch(() => {}); }, [open]);

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setBusy(false); if (fileRef.current) fileRef.current.value = ""; };
  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/assets/import-template", { params: { asset_type: assetType }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${assetType}-import-template.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const runPreview = async (f, mapObj) => {
    const reqId = ++reqIdRef.current;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("asset_type", assetType);
      if (mapObj) fd.append("mapping", JSON.stringify(mapObj));
      const { data } = await api.post("/assets/import", fd, { params: { commit: false }, headers: { "Content-Type": "multipart/form-data" } });
      if (reqId !== reqIdRef.current) return; // a newer preview superseded this one
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      toast.error(err.response?.data?.detail || "Failed to read CSV");
      reset();
    } finally {
      if (reqId === reqIdRef.current) setBusy(false);
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    await runPreview(f, null);
  };

  const changeMap = (fieldKey, header) => {
    const next = { ...mapping };
    if (header === NONE) delete next[fieldKey]; else next[fieldKey] = header;
    setMapping(next);
    if (file) runPreview(file, next);
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("asset_type", assetType);
      fd.append("mapping", JSON.stringify(mapping));
      const { data } = await api.post("/assets/import", fd, { params: { commit: true }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} asset${data.created === 1 ? "" : "s"}`);
      onImported?.();
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const typeKeys = Object.keys(types);

  const downloadIssues = () => {
    if (!preview?.issue_rows?.length) return;
    const cols = [...preview.headers, "issue_type", "reason"];
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [cols.join(",")];
    preview.issue_rows.forEach((r) => {
      const row = preview.headers.map((h) => esc(r.data[h]));
      row.push(esc(r.issue_type), esc(r.reason));
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "import-issues.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Import Assets</DialogTitle>
          <DialogDescription>Upload a CSV, then map your columns to Orbit fields. Your mapping is saved for next time.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs font-semibold text-zinc-600">Asset type</Label>
              <Select value={assetType} onValueChange={(v) => { setAssetType(v); reset(); }}>
                <SelectTrigger className="h-10 mt-1" data-testid="import-type"><SelectValue /></SelectTrigger>
                <SelectContent>{typeKeys.map((t) => <SelectItem key={t} value={t}>{types[t]?.label || t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="h-10 gap-2 border-zinc-300" data-testid="download-template">
              <DownloadSimple size={16} weight="bold" /> Template
            </Button>
          </div>

          <div
            className="border-2 border-dashed border-zinc-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="import-dropzone"
          >
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onPickFile} data-testid="import-file-input" />
            <FileCsv size={32} className="mx-auto text-emerald-500" weight="duotone" />
            <p className="text-sm font-medium mt-2">{file ? file.name : "Click to choose a CSV file"}</p>
            <p className="text-xs text-zinc-400 mt-1">UTF-8 CSV, up to a few thousand rows</p>
          </div>

          {busy && !preview && <div className="flex items-center gap-2 text-sm text-zinc-500"><Spinner size={18} className="animate-spin" /> Reading file…</div>}

          {preview && (
            <>
              <div className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid="import-mapping">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Map your columns</p>
                  <div className="flex items-center gap-2">
                    {preview.mapping_source === "saved" && !busy && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 rounded px-2 py-0.5" data-testid="saved-mapping-badge">Saved mapping applied</span>}
                    {busy && <Spinner size={16} className="animate-spin text-emerald-600" />}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
                  {preview.fields.map((f) => (
                    <div key={f.key} className="flex items-center gap-2 text-sm">
                      <span className="w-28 shrink-0 text-zinc-600">{f.label}</span>
                      <ArrowRight size={14} className="text-zinc-300 shrink-0" />
                      <Select value={mapping[f.key] || NONE} onValueChange={(v) => changeMap(f.key, v)}>
                        <SelectTrigger className="h-9 flex-1" data-testid={`map-${f.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— skip —</SelectItem>
                          {preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-zinc-200 p-3 space-y-2 text-sm" data-testid="import-preview">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1.5 font-semibold text-emerald-700"><CheckCircle size={18} weight="fill" /> {preview.valid} ready</span>
                  <span className="text-zinc-500">{preview.total_rows} rows total</span>
                  {preview.errors.length > 0 && <span className="flex items-center gap-1.5 text-red-600"><WarningCircle size={18} weight="fill" /> {preview.errors.length} errors</span>}
                  {preview.warnings.length > 0 && <span className="text-amber-600">{preview.warnings.length} warnings</span>}
                  {preview.issue_rows?.length > 0 && (
                    <Button variant="outline" size="sm" onClick={downloadIssues} className="h-8 gap-1.5 border-zinc-300 ml-auto" data-testid="download-issues">
                      <DownloadSimple size={14} weight="bold" /> Download issues
                    </Button>
                  )}
                </div>
                {(preview.errors.length > 0 || preview.warnings.length > 0) && (
                  <div className="max-h-28 overflow-y-auto text-xs space-y-1 border-t border-zinc-100 pt-2">
                    {preview.errors.map((e, i) => <div key={`e${i}`} className="text-red-600">Row {e.row}: {e.message}</div>)}
                    {preview.warnings.map((w, i) => <div key={`w${i}`} className="text-amber-600">Row {w.row}: {w.message}</div>)}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="border-zinc-300">Cancel</Button>
          <Button onClick={doImport} disabled={busy || !preview || preview.valid === 0} className="gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="confirm-import">
            {busy && preview ? <Spinner size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />}
            Import {preview ? `${preview.valid} assets` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
